import { useQuery } from '@tanstack/react-query';

import { ProviderService } from '../../services';
import { queryKeys } from '../keys';

/**
 * Polls `/api/v3/providers/health` for the status panel. Refetches every
 * 30s and stays fresh for 25s so the bottom-left dots are responsive
 * without hammering the BE.
 */
export function useProviderHealth() {
  return useQuery({
    queryKey: queryKeys.providerHealth(),
    queryFn: () => ProviderService.getHealth(),
    staleTime: 25_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    retry: 1,
  });
}

/**
 * Catalog of every registered provider (LLM + image) with their
 * `AvailableModels`. Used by `ProviderModelSelect` so the FE doesn't
 * hardcode the provider/model lists. Models change rarely → long stale
 * time + manual refetch only.
 */
export function useProviderCatalog() {
  return useQuery({
    queryKey: queryKeys.providerCatalog(),
    queryFn: () => ProviderService.getCatalog(),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

/**
 * Animation Lab tier-first model catalog (LLM/image's video peer). Feeds
 * `ModelTierPicker` so the FE never hardcodes the fal.ai model list — a BE
 * catalog refresh propagates without an FE deploy. Models churn monthly →
 * long stale time, manual refetch only.
 */
export function useVideoCatalog() {
  return useQuery({
    queryKey: queryKeys.videoCatalog(),
    queryFn: () => ProviderService.getVideoCatalog(),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

/**
 * Lists Ollama-installed models from the local daemon's `/api/tags`.
 * Used by the agent bar's model selector. Re-polls every 2 minutes —
 * model installs don't happen often, but `ollama pull` from another
 * terminal shouldn't require a refresh.
 */
export function useOllamaInstalledModels() {
  return useQuery({
    queryKey: queryKeys.ollamaInstalledModels(),
    queryFn: () => ProviderService.getOllamaInstalledModels(),
    staleTime: 60_000,
    refetchInterval: 120_000,
    retry: 1,
  });
}
